import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { FacilityProfileDetails } from 'src/app/locums/models/facility-profile-details.model';
import { JobDetails } from 'src/app/recruiting/jobs/details/models/job-details.model';
import { combine } from '../../shared/utilities/route';
import { AdditionalDetails } from '../models/additional-details.interface';
import { BoardCertification } from '../models/board-certification.model';
import { ControlledSubstance } from '../models/controlled-substance.model';
import { OtherRequirement } from '../models/other-requirement.interface';
import { PatientAgeGroup } from '../models/patient-age-group.interface';
import { StateLicense } from '../models/state-license.interface';
import { UnitCertification } from '../models/unit-certification.interface';
import { UnitDetails } from '../models/unit-details.model';
import { UnitPracticeSetting } from '../models/unit-practice-setting.interface';
import { VaccinationRequirements } from '../models/vaccination-requirements';
import { CorporateDirectoryUser } from '../models/corporate-directory-user.interface';

@Injectable({
    providedIn: 'root'
})
export class LocumsJobService {
    private patientAgeGroups: { [jobId: number]: PatientAgeGroup[] } = {};
    private practiceSettings: { [jobId: number]: UnitPracticeSetting[] } = {};
    private facilityProfileDetails: { [jobId: number]: FacilityProfileDetails } = {};
    private jobDetails: { [jobId: number]: JobDetails } = {};

    private readonly _coreApiPath: string;

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {
        this._coreApiPath = combine(this._settings.coreAPIUrl, 'ayanova');
    }

    getAdditionalDetails(jobId: number): Observable<AdditionalDetails> {
        const url = `${this._coreApiPath}/locums/job/${jobId}/additional-details`;
        return this._httpClient.get<AdditionalDetails>(url);
    }

    getBoardCertifications(jobId: number): Observable<BoardCertification[]> {
        const url = `${this._coreApiPath}/locums/job/${jobId}/board-certifications`;
        return this._httpClient.get<BoardCertification[]>(url);
    }

    getControlledSubstances(jobId: number): Observable<ControlledSubstance[]> {
        const url = `${this._coreApiPath}/locums/job/${jobId}/unit-controlled-substances`;
        return this._httpClient.get<ControlledSubstance[]>(url);
    }

    getUnitDetails(jobId: number): Observable<UnitDetails> {
        const url = `${this._coreApiPath}/locums/job/${jobId}/unit-details`;
        return this._httpClient.get<UnitDetails>(url);
    }

    getUnitPatientAgeGroups(jobId: number): Observable<PatientAgeGroup[]> {
        if (this.patientAgeGroups[jobId]) {
            return of(this.patientAgeGroups[jobId]);
        }

        const url = `${this._coreApiPath}/locums/job/${jobId}/unit-patient-age-groups`;
        return this._httpClient.get<PatientAgeGroup[]>(url).pipe(tap((x) => (this.patientAgeGroups[jobId] = x)));
    }

    getFacilityProfileDetails(jobId: number): Observable<FacilityProfileDetails> {
        if (this.facilityProfileDetails[jobId]) {
            return of(this.facilityProfileDetails[jobId]);
        }

        const url = `${this._coreApiPath}/locums/job/${jobId}/facility-profile-details`;
        return this._httpClient
            .get<FacilityProfileDetails>(url)
            .pipe(tap((x) => (this.facilityProfileDetails[jobId] = x)));
    }

    getStateLicenses(jobId: number): Observable<StateLicense[]> {
        const url = `${this._coreApiPath}/locums/job/${jobId}/unit-state-licenses`;
        return this._httpClient.get<StateLicense[]>(url);
    }

    getVaccinationRequirements(jobId: number): Observable<VaccinationRequirements[]> {
        const url = `${this._coreApiPath}/locums/job/${jobId}/vaccination-requirements`;
        return this._httpClient.get<VaccinationRequirements[]>(url);
    }

    getUnitCertifications(jobId: number): Observable<UnitCertification[]> {
        const url = `${this._coreApiPath}/locums/job/${jobId}/unit-certifications`;
        return this._httpClient.get<UnitCertification[]>(url);
    }

    getUnitOtherRequirements(jobId: number): Observable<OtherRequirement[]> {
        const url = `${this._coreApiPath}/locums/job/${jobId}/other-requirements`;
        return this._httpClient.get<OtherRequirement[]>(url);
    }

    getUnitPracticeSettings(jobId: number): Observable<UnitPracticeSetting[]> {
        if (this.practiceSettings[jobId]) {
            return of(this.practiceSettings[jobId]);
        }

        const url = `${this._coreApiPath}/locums/job/${jobId}/unit-practice-settings`;
        return this._httpClient.get<UnitPracticeSetting[]>(url).pipe(tap((x) => (this.practiceSettings[jobId] = x)));
    }

    getJobDetailsById(jobId: number): Observable<JobDetails> {
        if (this.jobDetails[jobId]) {
            return of(this.jobDetails[jobId]);
        }

        const url = `${this._coreApiPath}/jobs/${jobId}`;
        return this._httpClient.get<any>(url).pipe(tap((x) => (this.jobDetails[jobId] = x)));
    }

    getCorporateDirectoryUsers(): Observable<CorporateDirectoryUser[]> {
        const url = `${this._coreApiPath}/locums/job/corporate-directory-users`;
        return this._httpClient.get<CorporateDirectoryUser[]>(url);
    }

    getCorporateDirectoryUser(jobId: number): Observable<CorporateDirectoryUser> {
        const url = `${this._coreApiPath}/locums/job/${jobId}/corporate-directory-user`;
        return this._httpClient.get<CorporateDirectoryUser>(url);
    }
}
